import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, Package, Sparkles, CheckCircle, ArrowRight, 
  Upload, X, FileText, Calendar, User, Mail, Building2,
  MessageSquare, Loader2, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth, api, API } from '../lib/auth';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import axios from 'axios';

// All 46+ departments
const DEPARTMENTS = [
  { id: 'pod-intake', name: 'Workspace Intake & Project Management' },
  { id: 'branding', name: 'Branding & Identity' },
  { id: 'graphic-design', name: 'Graphic Design' },
  { id: 'video-production', name: 'Video Production' },
  { id: 'cinematic-ads', name: 'Cinematic Ad System' },
  { id: 'copywriting', name: 'Copywriting & Messaging' },
  { id: 'social-media', name: 'Social Media & Content' },
  { id: 'paid-ads', name: 'Paid Ads (Social/Search)' },
  { id: 'search-marketing', name: 'Search Marketing (PPC)' },
  { id: 'local-seo', name: 'Local SEO & Map Visibility' },
  { id: 'seo-content', name: 'SEO Content & Blogging' },
  { id: 'websites', name: 'Websites & Landing Pages' },
  { id: 'funnels', name: 'Funnels & Lead Capture' },
  { id: 'forms-surveys', name: 'Forms, Surveys & Quizzes' },
  { id: 'email-marketing', name: 'Email Marketing' },
  { id: 'sms-marketing', name: 'SMS & Text Marketing' },
  { id: 'marketing-automation', name: 'Marketing Automation' },
  { id: 'crm-setup', name: 'CRM Setup & Management' },
  { id: 'ai-agents', name: 'AI Agents & Chatbots' },
  { id: 'voice-ai', name: 'Voice AI & Phone Systems' },
  { id: 'reputation', name: 'Reputation Management' },
  { id: 'customer-success', name: 'Customer Success' },
  { id: 'analytics', name: 'Analytics & Reporting' },
  { id: 'appointment-setting', name: 'Appointment Setting' },
  { id: 'sales-support', name: 'Sales Support' },
  { id: 'podcast', name: 'Podcast Production' },
  { id: 'photography', name: 'Photography & Editing' },
  { id: 'print', name: 'Print & Merchandise' },
  { id: 'event-marketing', name: 'Event Marketing' },
  { id: 'influencer', name: 'Influencer Marketing' },
  { id: 'pr', name: 'PR & Media Relations' },
  { id: 'content-repurposing', name: 'Content Repurposing' },
  { id: 'scriptwriting', name: 'Scriptwriting' },
  { id: 'ux-ui', name: 'UX/UI Design' },
  { id: 'app-development', name: 'App Development' },
  { id: 'ecommerce', name: 'E-commerce Setup' },
  { id: 'membership', name: 'Membership Sites' },
  { id: 'webinar', name: 'Webinar Production' },
  { id: 'virtual-assistant', name: 'Virtual Assistant' },
  { id: 'data-entry', name: 'Data Entry & Research' },
  { id: 'bookkeeping', name: 'Bookkeeping Support' },
  { id: 'legal', name: 'Legal Document Prep' },
  { id: 'translation', name: 'Translation Services' },
  { id: 'accessibility', name: 'Accessibility & ADA' },
  { id: 'security', name: 'Security & Compliance' },
  { id: 'custom-integration', name: 'Custom Integration' },
];

const CONVERSATION_STEPS = [
  {
    id: 'welcome',
    type: 'message',
    content: "Hey there! Welcome to your Workspace! I'm here to help you submit a task. Let's go!",
  },
  {
    id: 'task_name',
    type: 'input',
    question: "What should we call this task?",
    placeholder: "e.g., Website Redesign, New Ad Campaign...",
    required: true,
    field: 'task_name',
  },
  {
    id: 'department',
    type: 'select',
    question: "Which department should handle this?",
    options: DEPARTMENTS,
    required: true,
    field: 'department',
  },
  {
    id: 'is_design',
    type: 'choice',
    question: "Is this a design task?",
    options: [
      { value: 'yes', label: '✅ Yes, design work' },
      { value: 'no', label: '❌ No design needed' },
      { value: 'partial', label: '🎨 Some design elements' },
    ],
    required: true,
    field: 'is_design_task',
  },
  {
    id: 'assets',
    type: 'textarea',
    question: "Any tools or assets needed? (one per line)\n\n⚠️ Don't include passwords here!",
    placeholder: "CRM\nZapier\nCanva...",
    required: false,
    field: 'assets_list',
  },
  {
    id: 'description',
    type: 'textarea',
    question: "Tell us about the task! 💡\n\nThe more detail, the better we can help!",
    placeholder: "Describe what you need done...",
    required: true,
    field: 'description',
  },
  {
    id: 'due_date',
    type: 'date',
    question: "When do you need this done?\n\n📅 Mon-Fri, 8:30AM-5:30PM CST",
    required: false,
    field: 'desired_due_date',
  },
  {
    id: 'files',
    type: 'file',
    question: "Got files to share? 📎\n\nLogos, images, docs - upload them here!",
    required: false,
    field: 'attachments',
  },
  {
    id: 'priority',
    type: 'choice',
    question: "How urgent is this?",
    options: [
      { value: 'low', label: '🟢 Low - No rush' },
      { value: 'medium', label: '🟡 Medium - Standard' },
      { value: 'high', label: '🔴 High - Urgent!' },
    ],
    required: true,
    field: 'priority',
  },
  {
    id: 'confirm',
    type: 'confirm',
    question: "Perfect! Ready to submit? 🚀",
  },
];

export default function ConversationalOrderWidget({ onOrderCreated }) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const initializedRef = useRef(false);

  // Stable scroll - only scroll within container, not the whole page
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Small delay to let content render first
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  useEffect(() => {
    // Initialize only once using ref
    if (!initializedRef.current) {
      initializedRef.current = true;
      const welcomeStep = CONVERSATION_STEPS[0];
      setMessages([{ type: 'bot', content: welcomeStep.content }]);
      setTimeout(() => {
        advanceToNextQuestion(1);
      }, 800);
    }
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentStep]);

  const advanceToNextQuestion = (stepIndex) => {
    if (stepIndex >= CONVERSATION_STEPS.length) {
      return;
    }
    const step = CONVERSATION_STEPS[stepIndex];
    if (step.type === 'message') {
      setMessages(prev => [...prev, { type: 'bot', content: step.content }]);
      setTimeout(() => advanceToNextQuestion(stepIndex + 1), 1000);
    } else {
      setMessages(prev => [...prev, { type: 'bot', content: step.question, stepType: step.type }]);
      setCurrentStep(stepIndex);
    }
  };

  const handleAnswer = (value) => {
    const step = CONVERSATION_STEPS[currentStep];
    
    let displayValue = value;
    if (step.type === 'select') {
      const option = step.options.find(o => o.id === value);
      displayValue = option?.name || value;
    } else if (step.type === 'choice') {
      const option = step.options.find(o => o.value === value);
      displayValue = option?.label || value;
    } else if (step.type === 'date') {
      displayValue = new Date(value).toLocaleDateString('en-US', { 
        weekday: 'short', month: 'short', day: 'numeric' 
      });
    } else if (step.type === 'file' && value?.length > 0) {
      displayValue = `📎 ${value.length} file(s) uploaded`;
    }
    
    setMessages(prev => [...prev, { type: 'user', content: displayValue }]);
    
    if (step.field) {
      setAnswers(prev => ({ ...prev, [step.field]: value }));
    }
    
    setInputValue('');
    setDepartmentSearch('');
    setShowDepartmentDropdown(false);
    
    setTimeout(() => {
      advanceToNextQuestion(currentStep + 1);
    }, 300);
  };

  // File upload handler
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    const uploadedUrls = [];
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.data?.url) {
          uploadedUrls.push(response.data.url);
        }
      }
      
      setUploadedFiles(prev => [...prev, ...uploadedUrls]);
      handleAnswer(uploadedUrls);
      toast.success(`${files.length} file(s) uploaded!`);
    } catch (error) {
      console.error('Upload error:', error);
      // Still proceed even if upload fails
      handleAnswer([]);
      toast.error('Upload failed, but you can add files later');
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const selectedDept = DEPARTMENTS.find(d => d.id === answers.department);
      
      const orderData = {
        title: answers.task_name,
        description: answers.description,
        department_name: selectedDept?.name || 'Workspace Intake & Project Management',
        client_name: user?.organization?.business_name || user?.organization?.name || user?.name || '',
        contact_email: user?.email || '',
        is_design_task: answers.is_design_task === 'yes' || answers.is_design_task === 'partial',
        assets_list: answers.assets_list || '',
        desired_due_date: answers.desired_due_date || null,
        priority: answers.priority || 'medium',
        source: 'conversational_widget',
        status: 'submitted',
        files: uploadedFiles,
        structured_data_json: {
          full_name: user?.name || '',
          client_name: user?.organization?.business_name || user?.organization?.name || user?.name || '',
          contact_email: user?.email || '',
        }
      };

      const response = await api.createOrder(orderData);
      
      setMessages(prev => [...prev, { 
        type: 'bot', 
        content: `✅ Done! Task "${answers.task_name}" submitted!\n\n📋 Order #${response.data.id?.slice(0, 8)}...\n\nWe're on it! Check your Orders dashboard for updates. 🎉`
      }]);
      
      setIsComplete(true);
      toast.success('Order submitted!');
      
      if (onOrderCreated) {
        onOrderCreated(response.data);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        content: `❌ Oops! Something went wrong. Try again?\n\nError: ${error.response?.data?.detail || error.message}`
      }]);
      toast.error('Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    const step = CONVERSATION_STEPS[currentStep];
    if (!step.required) {
      setMessages(prev => [...prev, { type: 'user', content: 'Skipped ⏭️' }]);
      setTimeout(() => {
        advanceToNextQuestion(currentStep + 1);
      }, 300);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const step = CONVERSATION_STEPS[currentStep];
      if (step.type === 'input' || step.type === 'textarea') {
        if (inputValue.trim() || !step.required) {
          handleAnswer(inputValue.trim());
        }
      }
    }
  };

  const resetForm = () => {
    setCurrentStep(0);
    setAnswers({});
    setInputValue('');
    setIsComplete(false);
    setMessages([]);
    setUploadedFiles([]);
    // Reset initialized ref and restart
    initializedRef.current = false;
    setTimeout(() => {
      initializedRef.current = true;
      const welcomeStep = CONVERSATION_STEPS[0];
      setMessages([{ type: 'bot', content: welcomeStep.content }]);
      setTimeout(() => {
        advanceToNextQuestion(1);
      }, 800);
    }, 100);
  };

  const filteredDepartments = DEPARTMENTS.filter(dept =>
    dept.name.toLowerCase().includes(departmentSearch.toLowerCase())
  );

  const currentStepData = CONVERSATION_STEPS[currentStep];

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-stretch justify-center" data-testid="conversational-order-widget">
      {/* Chat Widget - Blue/Cyan theme to match video */}
      <div className="w-full max-w-lg rounded-2xl overflow-hidden border border-cyan-500/40 shadow-2xl shadow-cyan-500/20 bg-gradient-to-b from-slate-900/95 via-slate-900/90 to-cyan-950/80">
        {/* Header - Blue gradient */}
        <div className="bg-gradient-to-r from-cyan-600/30 via-blue-600/20 to-cyan-600/30 p-4 border-b border-cyan-500/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                New Workspace Order
              </h3>
              <p className="text-sm text-cyan-200/60">AI-Powered Submission</p>
            </div>
          </div>
        </div>

        {/* Chat Messages - Fixed height matching video */}
        <div 
          ref={messagesContainerRef}
          className="h-[420px] overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-slate-900/50 to-cyan-950/30"
          style={{ scrollBehavior: 'smooth' }}
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={cn(
                "flex",
                msg.type === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div className={cn(
                "max-w-[85%] rounded-2xl px-4 py-2.5 shadow-lg",
                msg.type === 'user' 
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-br-md font-medium shadow-cyan-500/30' 
                  : 'bg-white/10 text-white rounded-bl-md backdrop-blur-sm border border-cyan-500/10'
              )}>
                <p className="whitespace-pre-wrap text-base leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {isSubmitting && (
            <div className="flex justify-start">
              <div className="bg-white/10 rounded-2xl rounded-bl-md px-4 py-2.5 backdrop-blur-sm border border-cyan-500/10">
                <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
              </div>
            </div>
          )}
        </div>

        {/* Input Area - Blue themed */}
        <div className="p-4 border-t border-cyan-500/20 bg-gradient-to-r from-slate-900/90 via-cyan-950/50 to-slate-900/90 backdrop-blur-sm">
          {isComplete ? (
            <div className="flex gap-2">
              <Button onClick={resetForm} className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-sm py-2">
                <Package className="w-4 h-4 mr-1" /> New Order
              </Button>
              <Button onClick={() => window.location.href = '/orders'} variant="outline" className="flex-1 text-sm py-2 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10">
                My Orders
              </Button>
            </div>
          ) : currentStepData?.type === 'confirm' ? (
            <div className="space-y-2">
              <div className="bg-cyan-500/10 rounded-xl p-2.5 text-sm border border-cyan-500/20">
                <p className="text-cyan-300 mb-1 text-xs">Summary:</p>
                <div className="space-y-0.5 text-gray-300 text-xs">
                  <p><span className="text-cyan-400/60">Name:</span> {answers.full_name}</p>
                  <p><span className="text-cyan-400/60">Business:</span> {answers.client_name}</p>
                  <p><span className="text-cyan-400/60">Task:</span> {answers.task_name}</p>
                  <p><span className="text-cyan-400/60">Dept:</span> {DEPARTMENTS.find(d => d.id === answers.department)?.name}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleConfirmSubmit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-sm py-2">
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                  Submit
                </Button>
                <Button onClick={resetForm} variant="outline" disabled={isSubmitting} className="text-sm py-2 border-cyan-500/30 text-cyan-300">
                  Reset
                </Button>
              </div>
            </div>
          ) : currentStepData?.type === 'input' ? (
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                type={currentStepData.inputType || 'text'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={currentStepData.placeholder}
                className="flex-1 bg-slate-800/80 border-cyan-500/30 text-white placeholder:text-cyan-200/40 focus:border-cyan-400 focus:ring-cyan-400/20 text-base py-2"
              />
              <Button 
                onClick={() => handleAnswer(inputValue.trim())}
                disabled={!inputValue.trim() && currentStepData.required}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white px-3"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          ) : currentStepData?.type === 'textarea' ? (
            <div className="space-y-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={currentStepData.placeholder}
                className="w-full h-20 px-3 py-2 bg-slate-800/80 border border-cyan-500/30 rounded-xl text-white text-base placeholder:text-cyan-200/40 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400 resize-none"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleAnswer(inputValue.trim())}
                  disabled={!inputValue.trim() && currentStepData.required}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-sm py-2"
                >
                  <Send className="w-4 h-4 mr-1" /> Send
                </Button>
                {!currentStepData.required && (
                  <Button onClick={handleSkip} variant="ghost" className="text-cyan-300/60 hover:text-cyan-300 text-sm">Skip</Button>
                )}
              </div>
            </div>
          ) : currentStepData?.type === 'select' ? (
            <div className="space-y-2">
              <div className="relative">
                <Input
                  value={departmentSearch}
                  onChange={(e) => { setDepartmentSearch(e.target.value); setShowDepartmentDropdown(true); }}
                  onFocus={() => setShowDepartmentDropdown(true)}
                  placeholder="Search departments..."
                  className="bg-slate-800/80 border-cyan-500/30 text-white placeholder:text-cyan-200/40 focus:border-cyan-400 pr-8 text-base"
                />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />
                
                {showDepartmentDropdown && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 bg-slate-900 border border-cyan-500/30 rounded-xl max-h-40 overflow-y-auto shadow-xl shadow-cyan-500/10 z-50">
                    {filteredDepartments.map((dept) => (
                      <button
                        key={dept.id}
                        onClick={() => handleAnswer(dept.id)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-cyan-500/20 hover:text-white transition-colors"
                      >
                        {dept.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : currentStepData?.type === 'choice' ? (
            <div className="space-y-1.5">
              {currentStepData.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className="w-full p-2.5 text-left text-sm rounded-xl bg-slate-800/60 border border-cyan-500/20 text-gray-300 hover:bg-cyan-500/20 hover:border-cyan-400/40 hover:text-white transition-all"
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : currentStepData?.type === 'date' ? (
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                type="date"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="flex-1 bg-slate-800/80 border-cyan-500/30 text-white focus:border-cyan-400 text-base"
              />
              <Button onClick={() => handleAnswer(inputValue)} disabled={!inputValue && currentStepData.required} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white px-3">
                <ArrowRight className="w-4 h-4" />
              </Button>
              {!currentStepData.required && (
                <Button onClick={handleSkip} variant="ghost" className="text-cyan-300/60 hover:text-cyan-300 text-sm">Skip</Button>
              )}
            </div>
          ) : currentStepData?.type === 'file' ? (
            <div className="space-y-2">
              <div className="border-2 border-dashed border-cyan-500/30 rounded-xl p-3 text-center hover:border-cyan-400/50 hover:bg-cyan-500/5 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    if (e.target.files?.length > 0) {
                      handleFileUpload(Array.from(e.target.files));
                    }
                  }}
                  className="hidden"
                  id="file-upload"
                  disabled={isUploading}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {isUploading ? (
                    <Loader2 className="w-6 h-6 mx-auto text-cyan-400 animate-spin" />
                  ) : (
                    <Upload className="w-6 h-6 mx-auto text-cyan-400 mb-1" />
                  )}
                  <p className="text-xs text-cyan-200/60">{isUploading ? 'Uploading...' : 'Tap to upload files'}</p>
                </label>
              </div>
              <Button onClick={handleSkip} variant="ghost" className="w-full text-cyan-300/60 hover:text-cyan-300 text-sm">
                Skip - add files later
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {/* AI Horse Video - Same height as widget, fills remaining space */}
      <div className="hidden lg:flex flex-1">
        <motion.video 
          src="https://customer-assets.emergentagent.com/job_c2ec4bf8-1159-4eee-836c-28f77c3b4053/artifacts/mcq6rav5_kling_20260123_Image_to_Video_AI_Horse_t_1414_0.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-[520px] rounded-2xl shadow-2xl shadow-cyan-500/30 border border-cyan-500/30 object-cover"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        />
      </div>
    </div>
  );
}
